import { Fragment } from "react";
import { clsx } from "clsx";
import { EmptyState } from "@/features/power_projects/components/ui";
import { formatDate } from "@/utils/formatDate";
import { JOURNAL_KIND_LABEL, type LedJournalRow } from "../types";

function fmt(iso: string): { day: string; time: string } {
  const d = new Date(iso);
  return {
    day: formatDate(d),
    time: d.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" }),
  };
}

const SIDE_TEXT = {
  gk: "text-[var(--color-primary)]",
  contractor: "text-blue-700 dark:text-blue-400",
  system: "text-[var(--color-text-muted)]",
} as const;

const RAIL = {
  gk: "var(--color-primary)",
  contractor: "#1d4ed8",
  system: "var(--color-border-strong)",
} as const;

interface Props {
  rows: LedJournalRow[];
  internal: boolean;
  contractorName: string;
  /** Moment przekazania — rysujemy go jako kreskę w osi czasu. */
  handedOverAt?: string | null;
}

/** Dziennik projektu: lewo GK ELEKTIM, prawo firma prowadząca, oś czasu pośrodku.
 *  Na telefonie jedna kolumna z podpisem strony. */
export function JournalSplit({ rows, internal, contractorName, handedOverAt }: Props) {
  if (rows.length === 0) return <EmptyState>Dziennik jest jeszcze pusty</EmptyState>;
  // Kreska „przekazanie projektu": u firmy zamyka listę (starszych wpisów nie dostaje),
  // u nas oddziela to, co było przed przekazaniem, od tego, co po nim.
  const handover = handedOverAt ? new Date(handedOverAt).getTime() : null;
  const markerIndex =
    handover === null
      ? -1
      : (() => {
          const i = rows.findIndex((r) => new Date(r.occurred_at).getTime() < handover);
          return i === -1 ? rows.length : i;
        })();
  const marker = (
    <li className="relative z-[1] flex items-center gap-3 py-3">
      <span className="h-px flex-1 bg-[var(--color-text-primary)]" />
      <span className="whitespace-nowrap border border-[var(--color-text-primary)] bg-[var(--color-bg-base)] px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest">
        przekazanie projektu · {handedOverAt ? new Date(handedOverAt).toLocaleDateString("pl-PL") : ""}
      </span>
      <span className="h-px flex-1 bg-[var(--color-text-primary)]" />
    </li>
  );
  return (
    <div>
      <div className="hidden md:grid md:grid-cols-[1fr_5.5rem_1fr] md:gap-4">
        <div className="border-b-2 border-[var(--color-primary)] pb-1 text-right font-mono text-[0.7rem] font-bold uppercase tracking-widest text-[var(--color-primary)]">
          GK ELEKTIM
        </div>
        <div className="pb-1 text-center font-mono text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]">oś czasu</div>
        <div className="truncate border-b-2 border-blue-700 pb-1 font-mono text-[0.7rem] font-bold uppercase tracking-widest text-blue-700 dark:border-blue-400 dark:text-blue-400">
          {contractorName}
        </div>
      </div>
      <ol className="relative mt-2 md:before:absolute md:before:bottom-0 md:before:left-1/2 md:before:top-0 md:before:w-px md:before:-translate-x-1/2 md:before:bg-[var(--color-border)]">
        {rows.map((row, idx) => {
          const { day, time } = fmt(row.occurred_at);
          const side = row.side;
          const card = (
            <div
              className={clsx(
                "bg-[var(--color-bg-surface)] px-3 py-2 text-[13px]",
                "border border-[var(--color-border)]",
                row.recent && "bg-amber-50/60 dark:bg-amber-950/20",
                row.internal_only && "border-dashed border-amber-500",
              )}
              style={{ borderLeft: `3px solid ${RAIL[side]}` }}
              title={row.actor_full_name ?? undefined}
            >
              <span className="block font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                {JOURNAL_KIND_LABEL[row.kind] ?? row.kind.replace(/_/g, " ")}
                {row.internal_only && " · tylko ELEKTIM"}
              </span>
              <span>{row.title}</span>
              {(row.parcel_teryt || row.owner_name) && (
                <span className="text-[var(--color-text-secondary)]"> · {[row.parcel_teryt, row.owner_name].filter(Boolean).join(" · ")}</span>
              )}
              {internal && row.detail && <span className="mt-0.5 block text-[12px] text-[var(--color-text-secondary)]">{row.detail}</span>}
              <span className={clsx("mt-0.5 block text-[11px]", SIDE_TEXT[side])}>{row.actor_label}</span>
            </div>
          );
          return (
            <Fragment key={row.id}>
            {idx === markerIndex && marker}
            <li className="grid grid-cols-1 gap-2 pb-2 md:grid-cols-[1fr_5.5rem_1fr] md:gap-4">
              <div className="hidden md:block">{side === "gk" ? card : null}</div>
              <div className="relative z-[1] flex items-center gap-2 md:flex-col md:justify-start md:gap-0.5 md:pt-1.5">
                <span className="bg-[var(--color-bg-base)] px-1 text-center font-mono text-[10px] leading-tight text-[var(--color-text-secondary)]">
                  {day}
                  <br className="hidden md:block" />
                  <span className="md:hidden"> </span>
                  {time}
                </span>
                <i className="inline-block h-2 w-2" style={{ background: RAIL[side] }} />
                <span className="font-mono text-[10px] uppercase text-[var(--color-text-muted)] md:hidden">
                  {side === "gk" ? "GK ELEKTIM" : side === "contractor" ? contractorName : "system"}
                </span>
              </div>
              <div className="md:contents">
                <div className="md:hidden">{card}</div>
                <div className="hidden md:block">{side !== "gk" ? card : null}</div>
              </div>
            </li>
            </Fragment>
          );
        })}
        {markerIndex === rows.length && marker}
      </ol>
    </div>
  );
}
